import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useTimeoutEffect } from '../../hooks/useTimeoutEffect';
import { selectors } from '../../modules/user';
import { My2NAppAccessIcon } from '../_Icons';
import messages from './messages';
import Panel from './Panel';

function getAvatarColor(isError: boolean, isEnabled: boolean, hasAuthKey: boolean) {
  if (isError) {
    return 'text.disabled';
  }
  if (isEnabled && hasAuthKey) {
    return 'success.main';
  }
  return 'primary.main';
}

interface IPanelMy2NAppAccess {
  isActivated: boolean;
}

function getStatusMessage(isError: boolean, isEnabled: boolean, isActivated: boolean) {
  if (isError) {
    return messages.panelMy2NAppAccessErrorLabel;
  }
  if (isEnabled && isActivated) {
    return messages.panelMy2NAppAccessStatusActivated;
  }
  return messages.panelMy2NAppAccessStatusEnabled;
}

function getStatusColor(isError: boolean, isEnabled: boolean, isActivated: boolean) {
  if (isError) {
    return 'text.disabled';
  }
  if (isEnabled && isActivated) {
    return 'success.main';
  }
  return 'primary.main';
}

function PanelMy2NAppAccess({ isActivated }: IPanelMy2NAppAccess) {
  const isCurrentlyActivated = useTimeoutEffect(isActivated);
  const { formatMessage } = useIntl();
  const { hasAuthKey, isDormakabaActivated, isDormakabaEnabled, isEnabled, isError } = {
    hasAuthKey: useSelector(selectors.hasUserMy2NAppAccessAuthKey),
    isDormakabaActivated: useSelector(selectors.isUserMy2NAppAccessDormakabaActivated),
    isDormakabaEnabled: useSelector(selectors.isUserMy2NAppAccessDormakabaEnabled),
    isEnabled: useSelector(selectors.getUserMy2NAppAccessEnabledStatus),
    isError: useSelector(selectors.isUserMy2NAppAccessStatusError),
  };

  const devicesStatusMessage = getStatusMessage(isError, isEnabled, hasAuthKey);
  const dormakabaStatusMessage = getStatusMessage(isError, isDormakabaEnabled, isDormakabaActivated);

  return (
    <Panel
      errorMessage={formatMessage(messages.panelMy2NAppAccessErrorMessage)}
      isActivated={isCurrentlyActivated}
      isError={isError}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Avatar sx={{ bgcolor: getAvatarColor(isError, isEnabled, hasAuthKey) }}>
          <My2NAppAccessIcon />
        </Avatar>
        {isError ? (
          <Typography sx={{ color: 'text.disabled' }} variant="body1">
            <FormattedMessage {...messages.panelMy2NAppAccessErrorLabel} />
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            <Typography sx={{ color: 'text.primary' }} variant="body1">
              <FormattedMessage {...messages.panelMy2NAppAccess2nDevicesLabel} />{' '}
              <Typography
                component="span"
                sx={{ color: getStatusColor(isError, isEnabled, hasAuthKey), fontWeight: 'bold' }}
                variant="body1"
              >
                <FormattedMessage {...devicesStatusMessage} values={{ box: (chunks) => <>{chunks}</> }} />
              </Typography>
            </Typography>
            {isDormakabaEnabled && (
              <Typography sx={{ color: 'text.primary' }} variant="body1">
                <FormattedMessage {...messages.panelMy2NAppAccessDormakabaLocksLabel} />{' '}
                <Typography
                  component="span"
                  sx={{ color: getStatusColor(isError, isDormakabaEnabled, isDormakabaActivated), fontWeight: 'bold' }}
                  variant="body1"
                >
                  <FormattedMessage {...dormakabaStatusMessage} values={{ box: (chunks) => <>{chunks}</> }} />
                </Typography>
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Panel>
  );
}

export default PanelMy2NAppAccess;
